import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Placard from './Placard';
import GalleryStrip from './GalleryStrip';
import SpecimenImage from './SpecimenImage';
import useGallery from './useGallery';
import useRenderedEntry from './useRenderedEntry';
import useThumbs from './useThumbs';
import { pruneThumbs } from './thumbs';
import { shareUrl } from './shareId';
import { shortId } from './format';

// Drawn, not a glyph — same reasoning as the placard's chevron.
const Arrow = ({ back }) => (
    <svg width="14" height="22" viewBox="0 0 14 22" fill="none" aria-hidden="true"
         style={back ? { transform: 'scaleX(-1)' } : undefined}>
        <path d="M3 2 L11 11 L3 20" stroke="currentColor" strokeWidth="1.6"
              strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// A swipe, not a tap and not a scroll. Both thresholds matter: the distance stops
// a sloppy tap from paging, and the horizontal-dominance test stops a vertical
// scroll gesture from doing it.
const SWIPE_MIN_PX = 48;

// How long a copied/confirm state stays on screen before reverting.
const TRANSIENT_MS = 2600;

// The gallery: one specimen at a time, full bleed, with the strip as its index.
//
// Chosen over a thumbnail grid for two reasons that happen to agree. The product
// reason: this is where people come back to look at what they found and to show
// someone else, and a grid of postage stamps is not that — the art should be as
// unencumbered here as it is in the generator. The engineering reason: only one
// full-size render is ever in flight, which is the cheap way past the render-cost
// trap that a grid of full-size renders would walk straight into.
const GalleryView = () => {

    const navigate = useNavigate();
    // Renamed on the way in: `rendering`/`failed` below belong to the CURRENT
    // SPECIMEN's render, while these two are about the gallery LIST arriving.
    const {
        entries, remove, retry, source,
        loading: galleryLoading,
        failed: galleryFailed
    } = useGallery();

    const [index, setIndex] = useState(0);
    const [copied, setCopied] = useState(false);
    const [shareFallback, setShareFallback] = useState(null);
    const [confirmRemove, setConfirmRemove] = useState(false);

    const count = entries.length;
    // Entries can disappear underneath us (a removal here, or another tab), so the
    // index is clamped at use rather than trusted.
    const safeIndex = count === 0 ? 0 : Math.min(index, count - 1);
    const entry = count === 0 ? null : entries[safeIndex];

    const entryId = entry ? entry.id : null;

    const { dataUrl, placeholder, rendering, failed } = useRenderedEntry(entry);
    const thumbs = useThumbs(entries, !rendering);

    const ids = useMemo(() => entries.map(e => e.id).join('|'), [entries]);

    // Removing a specimen should actually free its bytes, not leave the image
    // sitting in the cache until eviction gets round to it.
    useEffect(() => {
        pruneThumbs(ids ? ids.split('|') : []);
    }, [ids]);

    // Any transient affordance belongs to the specimen it was clicked on.
    useEffect(() => {
        setCopied(false);
        setShareFallback(null);
        setConfirmRemove(false);
    }, [entryId]);

    const go = (delta) => {
        setIndex(current => {
            const from = count === 0 ? 0 : Math.min(current, count - 1);
            return Math.min(Math.max(from + delta, 0), Math.max(0, count - 1));
        });
    };

    useEffect(() => {
        const onKeyDown = (event) => {
            // Never swallow keys aimed at a field or a focused control's own
            // activation; only bare navigation keys are ours.
            const tag = event.target && event.target.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

            if (event.key === 'ArrowRight') { event.preventDefault(); go(1); }
            else if (event.key === 'ArrowLeft') { event.preventDefault(); go(-1); }
            else if (event.key === 'Escape') { navigate('/'); }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [count, navigate]);

    const touchStart = useRef(null);

    const handleTouchStart = (event) => {
        const touch = event.touches[0];
        touchStart.current = { x: touch.clientX, y: touch.clientY };
    };

    const handleTouchEnd = (event) => {
        const start = touchStart.current;
        touchStart.current = null;
        if (!start) return;
        const touch = event.changedTouches[0];
        const dx = touch.clientX - start.x;
        const dy = touch.clientY - start.y;
        if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) <= Math.abs(dy)) return;
        go(dx < 0 ? 1 : -1);
    };

    // Clipboard access can be refused (insecure context, permissions, older
    // Safari). Falling back to showing the URL matters more than usual here: the
    // link IS the feature, so a silent failure would break sharing outright.
    const handleShare = async () => {
        const url = shareUrl(entry);
        if (!url) return;
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setShareFallback(null);
            window.setTimeout(() => setCopied(false), TRANSIENT_MS);
        } catch {
            setShareFallback(url);
        }
    };

    const handleRemove = () => {
        if (!confirmRemove) {
            setConfirmRemove(true);
            window.setTimeout(() => setConfirmRemove(false), TRANSIENT_MS);
            return;
        }
        // A deep zoom is effectively impossible to find again by hand — that is
        // the whole reason the gallery exists — so removal asks twice.
        remove(entry.id);
        setConfirmRemove(false);
    };

    // A signed-in gallery arrives over the network, so "empty" and "not here yet"
    // are now different things. Showing "Nothing kept yet" to someone whose
    // specimens are still in flight would tell them their collection is gone.
    if (galleryLoading) {
        return (
            <div id="gallery-empty">
                <p className="eyebrow">Your gallery</p>
                <h1 className="gallery-empty-title">Fetching your specimens&hellip;</h1>
            </div>
        );
    }

    // Nor is a failed fetch an empty gallery. Signed in means D1 is the only
    // truth (Block I), so there is nothing to quietly fall back to — say what
    // happened and offer the retry.
    if (galleryFailed) {
        return (
            <div id="gallery-empty">
                <p className="eyebrow">Your gallery</p>
                <h1 className="gallery-empty-title">Couldn&rsquo;t reach your gallery.</h1>
                <p className="gallery-empty-sub">
                    Your saved specimens are safe on your account &mdash; this browser
                    just couldn&rsquo;t fetch them. Nothing has been lost.
                </p>
                <button type="button" className="btn-begin" onClick={retry}>Try again</button>
            </div>
        );
    }

    if (count === 0) {
        return (
            <div id="gallery-empty">
                <p className="eyebrow">Your gallery</p>
                <h1 className="gallery-empty-title">Nothing kept yet.</h1>
                <p className="gallery-empty-sub">
                    Zoom into the set until something stops you, then save it from the
                    specimen placard. Saved specimens are coordinates, not files &mdash;
                    {source === 'account'
                        ? ' they live on your account, on whatever device you sign in from.'
                        : ' they stay in this browser until you make an account.'}
                </p>
                <Link to="/explore" className="btn-begin">Start zooming</Link>
            </div>
        );
    }

    return (
        <div id="gallery">
            <SpecimenImage
                dataUrl={dataUrl}
                placeholder={placeholder}
                rendering={rendering}
                failed={failed}
                alt={`Saved specimen ${shortId(entry.id)}`}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
            />

            <button
                type="button"
                className="gallery-page prev"
                onClick={() => go(-1)}
                disabled={safeIndex === 0}
                aria-label="Previous specimen"
            >
                <Arrow back />
            </button>
            <button
                type="button"
                className="gallery-page next"
                onClick={() => go(1)}
                disabled={safeIndex === count - 1}
                aria-label="Next specimen"
            >
                <Arrow />
            </button>

            <Placard current={entry} />

            <div id="gallery-actions">
                <span className="gallery-count">
                    <span className="placard-label">Your gallery</span>
                    <span className="placard-id">{safeIndex + 1} / {count}</span>
                </span>

                <button type="button" className="gallery-action" onClick={handleShare}>
                    {copied ? 'Link copied' : 'Copy link'}
                </button>

                <button
                    type="button"
                    className={`gallery-action${confirmRemove ? ' is-armed' : ''}`}
                    onClick={handleRemove}
                >
                    {confirmRemove ? 'Confirm remove' : 'Remove'}
                </button>

                {shareFallback &&
                    <input
                        className="gallery-share-fallback"
                        type="text"
                        readOnly
                        value={shareFallback}
                        aria-label="Shareable link"
                        onFocus={(event) => event.target.select()}
                    />
                }
            </div>

            <GalleryStrip
                entries={entries}
                thumbs={thumbs}
                activeId={entry.id}
                onSelect={setIndex}
            />
        </div>
    );
};

export default GalleryView;
